/**
 * RequestRedeliveryModal Component
 * 
 * Modal for requesting redelivery
 * Allows arbitrator to request the seller to redeliver an item
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import InfoIcon from '@mui/icons-material/Info';

interface RequestRedeliveryModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (payload: { reason: string; redeliveryType: string }) => void;
  disputeTitle: string;
  loading: boolean;
  error: string | null;
}

// Redelivery types
const REDELIVERY_TYPES = [
  { value: 'account_not_working', label: 'کالا معیوب است', description: 'کالای تحویل‌شده سالم یا قابل استفاده نیست' },
  { value: 'wrong_account', label: 'کالای اشتباه', description: 'کالا مطابق با سفارش نیست' },
  { value: 'missing_info', label: 'اطلاعات ناقص', description: 'اطلاعات یا اقلام سفارش کامل نیست' },
  { value: 'quality_issue', label: 'مشکل کیفیت', description: 'کالا مطابق توضیحات نیست' },
  { value: 'other', label: 'سایر', description: 'دلیل دیگر' }
];

const RequestRedeliveryModal: React.FC<RequestRedeliveryModalProps> = ({
  open,
  onClose,
  onConfirm,
  disputeTitle,
  loading,
  error
}) => {
  const [reason, setReason] = useState<string>('');
  const [redeliveryType, setRedeliveryType] = useState<string>('');

  const handleSubmit = () => {
    if (!redeliveryType || !reason.trim()) return;
    onConfirm({ reason, redeliveryType });
  };

  const handleClose = () => {
    if (!loading) {
      setReason('');
      setRedeliveryType('');
      onClose();
    }
  };

  const isValid = redeliveryType && reason.trim().length >= 5;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      dir="rtl"
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'warning.50'
      }}>
        <ReplayIcon color="warning" />
        <Typography variant="h6">درخواست تحویل مجدد</Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
          <Typography variant="body2">
            با ارسال درخواست تحویل مجدد، فروشنده ملزم به تحویل مجدد محصول خواهد بود.
            وضعیت آیتم به "در انتظار تحویل مجدد" تغییر می‌کند.
          </Typography>
        </Alert>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          اختلاف: <strong>{disputeTitle}</strong>
        </Typography>

        {/* Redelivery Type Select */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>نوع مشکل *</InputLabel>
          <Select
            value={redeliveryType}
            onChange={(e) => setRedeliveryType(e.target.value)}
            label="نوع مشکل *"
            disabled={loading}
          >
            {REDELIVERY_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    {type.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {type.description}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Reason TextField */}
        <TextField
          fullWidth
          label="توضیحات درخواست *"
          multiline
          rows={4}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="جزئیات مشکل و دلیل درخواست تحویل مجدد را وارد کنید..."
          disabled={loading}
          helperText="این توضیحات به فروشنده نمایش داده خواهد شد"
        />
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={handleClose} disabled={loading}>
          انصراف
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleSubmit}
          disabled={!isValid || loading}
          startIcon={loading ? <CircularProgress size={16} /> : <ReplayIcon />}
        >
          {loading ? 'در حال ارسال...' : 'ارسال درخواست'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RequestRedeliveryModal;

